import {
  CellValue,
  CostEstimate,
  CostEstimationMapping,
  CreateWorkflowNode,
  CreateWorkflowRequest,
  FillLLMTestDataResponse,
  NodeOutputDetailsResponse,
  NodeValidationExtraData,
  NodeValidationResult,
  RevisedWorkflow,
  UpdateWorkflowMetadataRequest,
  Workflow,
  WorkflowSummary,
  WorkflowExecutionFull,
  WorkflowExecutionSummary,
  WorkflowExecutionNode,
  WorkflowExecutionStatus,
  WorkflowNode,
  WorkflowNodeRevisionMappingResponse,
  WorkflowStatus,
  WorkflowExecution,
  WorkflowPromptNodeUsage,
  UpdateWorkflowDataProtectionRequest,
  BatchDeleteWorkflowsRequest,
  BatchDeleteWorkflowsResponse,
  PythonFunctionTestRunRequest,
  PythonFunctionTestRunResponse,
} from 'api/model';
import instance, { csvDownloadInstance } from '../axios_instance';
import { AxiosResponse } from 'axios';
import { FilterConfig } from '../../store/workflowExecutions';

// WORKFLOWS
export const getWorkflows = async (projectId: string): Promise<Workflow[]> => {
  const response = await instance.get<Workflow[]>(`/projects/${projectId}/workflows`);
  return response.data;
};

export const getWorkflowsSummary = async (projectId: string): Promise<WorkflowSummary[]> => {
  const response = await instance.get<WorkflowSummary[]>(`/projects/${projectId}/workflows?view=summary`);
  return response.data;
};

export type GetWorkflowsArgs = {
  projectId: string;
  pageParam?: number;
  pageSize?: number;
  sortDirection?: '' | '-';
  sortColumn?: string;
  filters?: FilterConfig[];
  workflowId?: string;
  view?: 'full' | 'summary';
};

export type GetWorkflowsArgsSummary = GetWorkflowsArgs & {
  view: 'summary';
};

export type GetWorkflowsArgsFull = GetWorkflowsArgs & {
  view?: 'full';
};

export function getWorkflowsPage(args: GetWorkflowsArgsFull): Promise<Workflow[]>;
export function getWorkflowsPage(args: GetWorkflowsArgsSummary): Promise<WorkflowSummary[]>;
export async function getWorkflowsPage({
  projectId,
  pageParam,
  pageSize = 50,
  sortDirection = '-',
  sortColumn = 'created_at',
  workflowId,
  filters,
  view,
}: GetWorkflowsArgs): Promise<Workflow[] | WorkflowSummary[]> {
  const searchParams = new URLSearchParams({
    page: pageParam?.toString() ?? '1',
    page_size: pageSize.toString(),
    sort: sortDirection + sortColumn,
  });

  if (workflowId) {
    searchParams.append('query', `id[eq]=${workflowId}`);
  }

  if (filters) {
    filters.forEach(
      (filter) => filter && searchParams.append('query', `${filter.attribute}[${filter.operator}]=${filter.value}`),
    );
  }

  if (view) {
    searchParams.append('view', view);
  }

  const response = await instance.get<Workflow[] | WorkflowSummary[]>(
    `/projects/${projectId}/workflows?${searchParams.toString()}`,
  );
  return response.data;
}

export const getWorkflow = async (
  id: string,
  revision: string | number | null,
  status?: WorkflowStatus,
): Promise<Workflow> => {
  let url = `/workflows/${id}`;
  if (revision != null) {
    url = `${url}/revisions/${revision}`;
  }
  if (status) {
    url += `?status=${status}`;
  }
  const response = await instance.get<Workflow>(url);
  return response.data;
};

export const getDraftWorkflow = async (id: string): Promise<Workflow> => {
  const url = `/workflows/${id}/draft`;

  const response = await instance.get<Workflow>(url);
  return response.data;
};

export const deleteDraftWorkflow = async (id: string): Promise<Workflow> => {
  return await instance.delete(`/workflows/${id}/draft`);
};

export const getWorkflowsBatch = async (workflows: RevisedWorkflow[]): Promise<Workflow[]> => {
  const response = await instance.post<Workflow[]>('/workflows/batch-get', workflows);
  return response.data;
};

export const createWorkflow = async (projectId: string, workflow: CreateWorkflowRequest): Promise<Workflow> => {
  const response = await instance.post<Workflow>(`/projects/${projectId}/workflows`, {
    ...workflow,
    status: 'PUBLISHED',
  });
  return response.data;
};

export const removeWorkflow = async (id: string) => {
  return await instance.delete(`/workflows/${id}`);
};

export const batchDeleteWorkflows = async (
  request: BatchDeleteWorkflowsRequest,
): Promise<BatchDeleteWorkflowsResponse> => {
  const response = await instance.post<BatchDeleteWorkflowsResponse>('/workflows/batch-delete', request);
  return response.data;
};

export const executeWorkflow = async (
  id: string,
  workflowRevision: string | null | undefined | number,
  testMode: boolean,
  parameters: Record<string, string>,
) => {
  let url = `/workflows/${id}`;
  if (workflowRevision) {
    url += `/revisions/${workflowRevision}`;
  }
  if (testMode) {
    url += '?test_mode=true';
  }
  return await instance.post<WorkflowExecution>(url, { parameters });
};

export const workflowCostEstimation = async (id: string, mapping: CostEstimationMapping) => {
  const response = await instance.post<CostEstimate>(`/workflows/${id}/cost-estimation`, { mapping });
  return response.data;
};

export const getWorkflowLLMNodes = async (id: string, llm = true): Promise<WorkflowNode[]> => {
  const response = await instance.get<WorkflowNode[]>(`/workflows/${id}/nodes`, {
    params: { llm },
  });
  return response.data;
};

export const updateWorkflow = async (
  projectId: string,
  id: string,
  workflow: CreateWorkflowRequest,
): Promise<Workflow> => {
  const response = await instance.put<Workflow>(`/projects/${projectId}/workflows/${id}`, workflow);
  return response.data;
};

export const updateWorkflowMetadata = async (
  projectId: string,
  id: string,
  request: UpdateWorkflowMetadataRequest,
): Promise<Workflow> => {
  const response = await instance.patch<Workflow>(`/projects/${projectId}/workflows/${id}/metadata`, request);
  return response.data;
};

export const updateWorkflowDataProtection = async (
  projectId: string,
  id: string,
  request: UpdateWorkflowDataProtectionRequest,
): Promise<Workflow> => {
  const response = await instance.patch<Workflow>(`/projects/${projectId}/workflows/${id}/data-protection`, request);
  return response.data;
};

export type GetWorkflowExecutionsArgs = {
  projectId: string;
  pageParam?: number;
  pageSize?: number;
  sortDirection?: '' | '-';
  sortColumn?: string;
  filters?: FilterConfig[];
  workflowId?: string;
  view?: 'full' | 'extended' | 'summary';
};

export type GetWorkflowExecutionsArgsSummary = GetWorkflowExecutionsArgs & {
  view: 'summary';
};

export type GetWorkflowExecutionsArgsExtended = GetWorkflowExecutionsArgs & {
  view: 'extended';
};

export type GetWorkflowExecutionsArgsFull = GetWorkflowExecutionsArgs & {
  view?: 'full';
};

export function getWorkflowExecutions(args: GetWorkflowExecutionsArgsFull): Promise<WorkflowExecutionFull[]>;
export function getWorkflowExecutions(args: GetWorkflowExecutionsArgsExtended): Promise<WorkflowExecution[]>;
export function getWorkflowExecutions(args: GetWorkflowExecutionsArgsSummary): Promise<WorkflowExecutionSummary[]>;
export async function getWorkflowExecutions({
  projectId,
  pageParam,
  pageSize = 50,
  workflowId,
  sortDirection = '-',
  sortColumn = 'trigger_time',
  filters,
  view,
}: GetWorkflowExecutionsArgs): Promise<WorkflowExecutionFull[] | WorkflowExecutionSummary[] | WorkflowExecution[]> {
  const searchParams = new URLSearchParams({
    page: pageParam?.toString() ?? '1',
    page_size: pageSize.toString(),
    sort: sortDirection + sortColumn,
  });

  if (workflowId) {
    searchParams.append('query', `workflow_id[eq]=${workflowId}`);
  }

  if (filters) {
    filters.forEach(
      (filter) => filter && searchParams.append('query', `${filter.attribute}[${filter.operator}]=${filter.value}`),
    );
  }

  if (view) {
    searchParams.append('view', view);
  }

  const response = await instance.get<WorkflowExecutionFull[] | WorkflowExecutionSummary[] | WorkflowExecution[]>(
    `/projects/${projectId}/workflow-executions?${searchParams.toString()}`,
  );
  return response.data;
}

export const getWorkflowExecution = async (id: string): Promise<WorkflowExecutionFull> => {
  const response = await instance.get<WorkflowExecutionFull>(`/workflow-executions/${id}`);
  return response.data;
};

export const getLastCompletedWorkflowExecution = async (
  projectId: string,
  workflowId: string,
): Promise<WorkflowExecutionFull | null> => {
  const executions = await getWorkflowExecutions({
    projectId,
    pageParam: 1,
    pageSize: 1,
    workflowId,
    sortDirection: '-', // descending
    sortColumn: 'trigger_time',
    filters: [
      {
        attribute: 'status',
        operator: 'eq',
        value: 'COMPLETED',
      },
    ],
  });
  return executions[0] ?? null;
};

export const getLatestWorkflowExecution = async (workflowId: string): Promise<WorkflowExecutionSummary | null> => {
  const response = await instance.get<WorkflowExecutionSummary | null>(`/workflows/${workflowId}/latest-execution`);
  return response.data;
};

export const getWorkflowExecutionNodesStatus = async (id: string): Promise<WorkflowExecutionNode[]> => {
  const response = await instance.get<WorkflowExecutionNode[]>(`/workflow-executions/${id}/nodes/status`);
  return response.data;
};

export const getWorkflowExecutionNodeStatus = async (
  executionId: string,
  nodeId: string,
): Promise<WorkflowExecutionNode> => {
  const response = await instance.get<WorkflowExecutionNode>(
    `/workflow-executions/${executionId}/nodes/${nodeId}/status`,
  );
  return response.data;
};

export const getWorkflowExecutionStatus = async (id: string): Promise<WorkflowExecutionStatus> => {
  const response = await instance.get<WorkflowExecutionStatus>(`/workflow-executions/${id}/status`);
  return response.data;
};

export const cancelWorkflowExecution = async (id: string): Promise<WorkflowExecutionStatus> => {
  const response = await instance.post<WorkflowExecutionStatus>(`/workflow-executions/${id}/cancel`);
  return response.data;
};

export const getNodeOutputData = async ({
  workflowExecutionId,
  nodeId,
  pageParam,
  searchText,
  searchColumn,
  pageSize = 100,
  sortColumn = 'giq_index',
  sortDirection = '',
  previewSize = undefined,
}: {
  workflowExecutionId: string;
  nodeId: string;
  pageParam?: number;
  pageSize?: number;
  sortColumn: string;
  sortDirection: string;
  searchText?: string;
  searchColumn?: string;
  previewSize?: number;
}): Promise<NodeOutputDetailsResponse> => {
  const searchParams = new URLSearchParams({
    page: pageParam?.toString() ?? '1',
    page_size: pageSize.toString(),
    sort: sortDirection + sortColumn,
  });

  if (searchText && searchColumn) {
    searchParams.append('query', `${searchColumn}[ilike]=${searchText}`);
  }
  if (previewSize) {
    searchParams.append('preview_size', `${previewSize}`);
  }

  const response = await instance.get<NodeOutputDetailsResponse>(
    `/workflow-executions/${workflowExecutionId}/nodes/${nodeId}/output-data?${searchParams.toString()}`,
  );
  return response.data;
};

export const getNodeOutputDataColumns = async ({
  workflowExecutionId,
  nodeId,
}: {
  workflowExecutionId: string;
  nodeId: string;
}): Promise<string[]> => {
  const response = await instance.get<string[]>(
    `/workflow-executions/${workflowExecutionId}/nodes/${nodeId}/output-data-columns`,
  );
  return response.data;
};

export const getNodeCellOutputData = async ({
  workflowExecutionId,
  nodeId,
  index,
  column,
}: {
  workflowExecutionId: string;
  nodeId: string;
  index: number;
  column: string;
}): Promise<CellValue> => {
  const params = new URLSearchParams({
    index: index.toString(),
    column,
  });

  const response = await instance.get<CellValue>(
    `/workflow-executions/${workflowExecutionId}/nodes/${nodeId}/cell-output-data?${params.toString()}`,
  );
  return response.data;
};

export const downloadNodeOutputDataFile = async ({
  workflowExecutionId,
  nodeId,
  setProgress,
  sortColumn,
  sortDirection,
  searchText,
  searchColumn,
  abortController,
}: {
  workflowExecutionId: string;
  nodeId: string;
  setProgress: (progress: number) => void;
  sortColumn?: string;
  sortDirection?: string;
  searchText?: string;
  searchColumn?: string;
  abortController?: AbortController;
}): Promise<void> => {
  const searchParams = new URLSearchParams();

  if (sortColumn) {
    searchParams.append('sort', (sortDirection ?? '') + sortColumn);
  }

  if (searchText && searchColumn) {
    searchParams.append('query', `${searchColumn}[ilike]=${searchText}`);
  }

  const response: AxiosResponse<Blob> = await csvDownloadInstance.get(
    `/workflow-executions/${workflowExecutionId}/nodes/${nodeId}/output-data/file?${searchParams.toString()}`,
    {
      onDownloadProgress: (progressEvent) => setProgress(Math.round((progressEvent?.progress ?? 0) * 100)),
      signal: abortController?.signal,
    },
  );

  const contentDisposition: string | undefined = response.headers['content-disposition'] as string | undefined;
  const filename = contentDisposition?.match(/filename="?([^"]+)"?/)?.[1] ?? 'downloaded-file';

  const url = window.URL.createObjectURL(response.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const downloadNodeLogsFile = async (executionId: string, nodeId: string): Promise<Blob> => {
  const response = await instance.get<Blob>(`/workflow-executions/${executionId}/nodes/${nodeId}/logs`, {
    responseType: 'blob',
  });
  return response.data;
};

export const getLatestWebsiteExport = async (workflowId: string): Promise<WorkflowExecutionNode> => {
  const response = await instance.get<WorkflowExecutionNode>(`/workflows/${workflowId}/latest-website-export`);
  return response.data;
};

export const getParentWorkflows = async (workflowId: string): Promise<Workflow[]> => {
  const response = await instance.get<Workflow[]>(`/workflows/${workflowId}/parent-workflows`);
  return response.data;
};

export const getParentWorkflowsNodes = async (workflowId: string): Promise<WorkflowNode[]> => {
  const response = await instance.get<WorkflowNode[]>(`/workflows/${workflowId}/parent-workflows-nodes`);
  return response.data;
};

export const validateNode = async (
  node: CreateWorkflowNode,
  projectId: string,
  extra_data: NodeValidationExtraData = null,
): Promise<NodeValidationResult> => {
  const response = await instance.post<NodeValidationResult>('/nodes/validate', {
    node,
    project_id: projectId,
    extra_data,
  });
  return response.data;
};

export const getRevisedNodesMapping = async (
  workflowId: string,
  sourceRevision: number,
  targetRevision: number,
  nodeIds: string[],
): Promise<WorkflowNodeRevisionMappingResponse> => {
  const params = new URLSearchParams({
    source_revision: sourceRevision.toString(),
    target_revision: targetRevision.toString(),
  });
  nodeIds.forEach((id) => params.append('node_ids', id));

  const response = await instance.get<WorkflowNodeRevisionMappingResponse>(
    `/workflows/${workflowId}/revised-nodes-mapping?${params.toString()}`,
  );
  return response.data;
};

export const restoreWorkflowRevision = async (workflowId: string, revision: number): Promise<Workflow> => {
  const response = await instance.post<Workflow>(`/workflows/${workflowId}/revisions/${revision}/restore`);
  return response.data;
};

export const fillLLMTestData = async (workflowId: string): Promise<FillLLMTestDataResponse> => {
  const response = await instance.post<FillLLMTestDataResponse>(`/workflows/${workflowId}/fill-llm-nodes-test-data`);
  return response.data;
};

export const getWorkflowNodesCompletedExecutionsByPrompt = async (
  projectId: string,
  workflowId: string,
  promptId: string,
  executionLimit = 20,
): Promise<WorkflowPromptNodeUsage> => {
  const params = new URLSearchParams({
    execution_limit: executionLimit.toString(),
  });

  const response = await instance.get<WorkflowPromptNodeUsage>(
    `/projects/${projectId}/workflows/${workflowId}/prompt/${promptId}/completed-node-executions?${params.toString()}`,
  );
  return response.data;
};

export const pythonFunctionTestRun = async (
  request: PythonFunctionTestRunRequest,
): Promise<PythonFunctionTestRunResponse> => {
  const response = await instance.post<PythonFunctionTestRunResponse>('/nodes/python-function/test-run', request);
  return response.data;
};

export type LiveExecutionsParams = {
  limit?: number;
  recentMinutes?: number;
  includeTestMode?: boolean;
  view?: 'full' | 'extended' | 'summary';
};

export type LiveExecutionsParamsSummary = LiveExecutionsParams & {
  view: 'summary';
};

export type LiveExecutionsParamsExtended = LiveExecutionsParams & {
  view: 'extended';
};

export type LiveExecutionsParamsFull = LiveExecutionsParams & {
  view?: 'full';
};

export function getLiveExecutions(params?: LiveExecutionsParamsFull): Promise<WorkflowExecutionFull[]>;
export function getLiveExecutions(params: LiveExecutionsParamsExtended): Promise<WorkflowExecution[]>;
export function getLiveExecutions(params: LiveExecutionsParamsSummary): Promise<WorkflowExecutionSummary[]>;
export async function getLiveExecutions({
  limit = 20,
  recentMinutes,
  includeTestMode,
  view,
}: LiveExecutionsParams = {}): Promise<WorkflowExecutionFull[] | WorkflowExecution[] | WorkflowExecutionSummary[]> {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (recentMinutes !== undefined) params.set('recent_minutes', recentMinutes.toString());
  if (includeTestMode) params.set('include_test_mode', 'true');
  if (view) params.set('view', view);

  const response = await instance.get<WorkflowExecutionFull[] | WorkflowExecution[] | WorkflowExecutionSummary[]>(
    `/workflow-executions/live?${params.toString()}`,
  );
  return response.data;
}
